import React, { useRef, useCallback, useState, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, IconButton, Tooltip, Chip, Typography, Paper, Button, Stack,
  Popper, ClickAwayListener, List, ListItem, ListItemButton, ListItemText,
  CircularProgress, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Add as AddIcon,
  CurrencyExchange as CurrencyExchangeIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import productoService, { type EquivalenciasUmDetalle } from '../../../services/productoService';
import {
  evaluarUsoProductoDocumento,
  esDiscontinuadoBloqueadoParaAgregar,
  esTipoDiscontinuado,
  filtrarProductosInactivos,
  mensajeArticuloDiscontinuadoBloqueado,
  obtenerProductoParaDocumento,
  opcionesGetProductoDocumento,
  paramsBusquedaProductoDocumento,
  type ModoBloqueoArtDiscontinuados,
} from '../../../utils/bloqueaArtDiscontinuados';
import articuloXListaPrecioService from '../../../services/articuloXListaPrecioService';
import { resolverPrecioDesdeLista, obtenerPrecioBaseProducto } from '../../../utils/precioListaPedido';
import { resolverCodigoMonedaProducto, resolverPrecioPedidoConMoneda } from '../../../utils/pedidoVentaMoneda';
import { useCotizacionDiaGuard } from '../../../hooks/useCotizacionDiaGuard';
import {
  type ContextoUmVenta,
  factorCantidadEntreUm,
  listarUmsConEquivalencias,
  mismaUm,
  precioEnUmDocumento,
  resolverUmYPrecioDocumento,
  resolverUmYPrecioDesdeDetalle,
} from '../../../utils/umVentaDocumento';
import { fetchStockDisponibleEnUm } from '../../../utils/stockUmVenta';
import { formatCurrency, formatStockCantidad } from '../../../utils/formatters';
import {
  cantidadDoc,
  labelsExtra,
  showExtraPedFac,
  type UmSnapshotKind,
  umDoc,
} from '../../../utils/umDocumentoDisplay';
import { POSLinea, SuggestionItem } from './types';
import POSLineaUmChips from './POSLineaUmChips';
import {
  type ContextoDescuentoVenta,
  resolverPorcDescLinea,
} from '../../../services/descuentoService';
import FacturaPOSStockPopover from './FacturaPOSStockPopover';
import { getApiErrorMessage } from '../../../utils/apiError';
import { esEntradaCodigoBarra } from '../../../utils/codigoBarraVenta';
import {
  aPrecioDisplay,
  aplicarPorcentajeDescuento,
  multiplicadorIvaProducto,
  round2,
  subtotalLineaConIvaDisplay,
} from './preciosIvaDisplay';
import CantidadesPedidoDialog from './CantidadesPedidoDialog';
import { formatCantidadPedido } from '../../../utils/cantidadesPedido';
import POSPrecioUnitarioField from './POSPrecioUnitarioField';
import { esCondIvaMonotributo } from '../../../constants/condIvaCompania';
import ProductoInfo360Dialog from '../../inventario/info360/ProductoInfo360Dialog';
import ProductoInfo360ListButton from '../../inventario/info360/ProductoInfo360ListButton';
import LoteLineaButton from '../../../components/lotes/LoteLineaButton';
import KitLotesLineaButton from '../../../components/lotes/KitLotesLineaButton';
import { getLoteChipStatus } from '../../../components/lotes/LoteChip';
import {
  buildKitLotesComponentes,
  extractLoteFlagsFromProducto,
  productoControlaLote,
  type KitLoteComponenteLinea,
  type LoteMode,
} from '../../../utils/loteLineaHelpers';
import type { LoteAsignacion } from '../../../services/loteService';

interface Props {
  lineas: POSLinea[];
  nroLista?: string;
  /** Cabecera para que el backend resuelva el % de descuento (cliente, ruta, lista, etc.). */
  contextoDescuento?: ContextoDescuentoVenta;
  /** UM de logística a usar en la línea: pedido→umrefPed, factura→umrefFac. */
  contextoUm?: ContextoUmVenta;
  fechaEmision?: Date | string;
  aplicarCotizacionMoneda?: boolean;
  tipoIvaEmision?: string;
  condIvaEmisor?: string | null;
  productosIvaCache?: Record<string, unknown>;
  preciosConIva?: boolean;
  readOnly?: boolean;
  /** Muestra precioUnitarioFinal/subtotalFinal del documento cargado (edición/lectura). */
  usarPreciosPersistidosEnLineas?: boolean;
  onLineaChange: (id: string, field: keyof POSLinea, value: string | number) => void;
  onLineaFilled: (id: string, data: Partial<POSLinea>) => void;
  /** Registra el producto completo para cálculo de IVA (evita esperar al useEffect de cabecera). */
  onProductoIvaLoaded?: (codigoArticulo: string, producto: unknown) => void;
  onEliminarLinea: (id: string) => void;
  onDuplicarLinea: (id: string) => void;
  onAgregarLineaVacia: () => void;
  /** Texto vacío de la grilla (p. ej. pedido / cotización / remito). */
  emptyHint?: string;
  /** Filtra búsqueda/alta por TipoArticulo (coma-separados), p. ej. "FIN". */
  tiposArticulo?: string;
  /** Muestra columna % Conv. (solo pedidos de venta). */
  enablePorcConversion?: boolean;
  /** Modal y columnas En espera / Cancelada (solo pedidos de venta). */
  enableCantidadesPedido?: boolean;
  /** Si se informa, habilita chip/dialog de lote en renglones con control L/S. */
  loteAsignacion?: {
    mode: LoteMode;
    nroUbicacion: number;
  } | null;
}

const NUMERIC_COLS = ['cantidad', 'descuento', 'porcConversion'];
const NAV_COLS_BASE = ['codigoArticulo', 'descripcionArticulo', 'cantidad', 'precioUnitario', 'descuento', 'notas'];
const MSG_CODIGO_INEXISTENTE = 'No existe en el maestro de artículos';

const tipoArticuloPermitidoEnLinea = (
  tipo: string | null | undefined,
  tiposArticulo?: string,
): boolean => {
  const allowed = String(tiposArticulo ?? '')
    .split(',')
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);
  if (allowed.length === 0) return true;
  return allowed.includes(String(tipo ?? '').trim().toUpperCase());
};

const mensajeTipoArticuloNoPermitido = (tiposArticulo?: string) =>
  tiposArticulo
    ? `Solo se permiten artículos de tipo ${tiposArticulo}.`
    : 'Tipo de artículo no permitido.';

const lineaArticuloResuelto = (linea: POSLinea) =>
  Boolean(String(linea.descripcionArticulo ?? '').trim());

const CantidadPedidoMarca = ({ valor, tono }: { valor: number; tono: 'espera' | 'cancelada' }) => {
  if (!(valor > 0)) {
    return (
      <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
        0
      </Box>
    );
  }
  const espera = tono === 'espera';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        px: 0.75,
        py: '1px',
        borderRadius: '4px',
        fontSize: '0.8rem',
        fontWeight: 700,
        lineHeight: 1.5,
        bgcolor: espera ? '#FFF8E1' : '#FDECEA',
        color: espera ? '#E65100' : '#C62828',
      }}
    >
      {formatCantidadPedido(valor)}
    </Box>
  );
};

const datosArticuloVacios = (): Partial<POSLinea> => ({
  descripcionArticulo: '',
  precioUnitario: 0,
  precioUmRef: undefined,
  umPrecioRef: undefined,
  precioOriginalMoneda: undefined,
  codigoMonedaPrecio: undefined,
  cotizacionMonedaPrecio: undefined,
  precioConvertidoBaseMoneda: undefined,
  precioMinimo: null,
  um: '',
  stockDisponible: null,
  controlLoteSerie: 'N',
  requiereVencimiento: false,
  lotes: undefined,
  lotesOrigenFactura: undefined,
  esKit: false,
  lotesComponentes: undefined,
});

/** Precio visible + ancla para cambios de UM sin acumular redondeo. */
const precioConAnclaUm = (
  precioUnitario: number,
  um: string,
  precioUmRef?: number,
  umPrecioRef?: string,
): Pick<POSLinea, 'precioUnitario' | 'precioUmRef' | 'umPrecioRef'> => ({
  precioUnitario,
  precioUmRef: precioUmRef ?? precioUnitario,
  umPrecioRef: (umPrecioRef || um || '').trim() || um,
});

const loteFlagsParaLinea = (producto: unknown): Pick<POSLinea, 'controlLoteSerie' | 'requiereVencimiento' | 'lotes'> => {
  const flags = extractLoteFlagsFromProducto(producto);
  return {
    controlLoteSerie: flags.controlLoteSerie,
    requiereVencimiento: flags.requiereVencimiento,
    lotes: undefined,
  };
};

const precioMinimoDesdeProducto = (producto: unknown): number | null => {
  if (!producto || typeof producto !== 'object') return null;
  const raw = (producto as Record<string, unknown>).precioMinimo;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const EMPTY_PRODUCTOS_CACHE: Record<string, unknown> = {};

async function resolverUmPrecioLinea(
  producto: unknown,
  precioBase: number,
  contextoUm: ContextoUmVenta,
  umPrecioLista?: string | null,
): Promise<{ um: string; precioUnitario: number; precioUmRef: number; umPrecioRef: string }> {
  const codigo = String((producto as { codigoArticulo?: string } | null)?.codigoArticulo ?? '').trim();
  if (codigo && contextoUm !== 'base') {
    try {
      const detalle = await productoService.getEquivalenciasUmDetalle(codigo);
      const r = resolverUmYPrecioDesdeDetalle(
        producto,
        precioBase,
        contextoUm,
        detalle,
        umPrecioLista,
      );
      const umRef = String(umPrecioLista || r.umBase || r.um || '').trim();
      return {
        um: r.um,
        precioUnitario: round2(r.precioUnitario),
        precioUmRef: Number(precioBase) || 0,
        umPrecioRef: umRef,
      };
    } catch {
      /* fallback abajo */
    }
  }
  const r = resolverUmYPrecioDocumento(producto, precioBase, contextoUm, [], umPrecioLista);
  const umRef = String(umPrecioLista || r.umBase || r.um || '').trim();
  return {
    um: r.um,
    precioUnitario: round2(r.precioUnitario),
    precioUmRef: Number(precioBase) || 0,
    umPrecioRef: umRef,
  };
}

/** Muestra el contenido completo del campo al pasar el mouse (como en Buscar pendientes). */
const CampoConTooltip: React.FC<{
  title?: string | null;
  children: React.ReactElement;
}> = ({ title, children }) => {
  const texto = String(title ?? '').trim();
  return (
    <Tooltip
      title={texto}
      disableHoverListener={!texto}
      placement="top"
      enterDelay={400}
      slotProps={{
        tooltip: { sx: { maxWidth: 420, whiteSpace: 'pre-wrap' } },
      }}
    >
      <Box component="span" sx={{ display: 'block', width: '100%' }}>
        {children}
      </Box>
    </Tooltip>
  );
};

const StockBadge: React.FC<{ stock?: number | null; onClick?: (e: React.MouseEvent<HTMLElement>) => void }> = ({ stock, onClick }) => {
  const sx = {
    height: 18,
    fontSize: '0.65rem',
    cursor: onClick ? 'pointer' : 'default',
    '& .MuiChip-label': { px: 0.75 },
  };
  if (stock == null)
    return <Chip label="?" size="small" variant="outlined" onClick={onClick} sx={sx} />;
  if (stock <= 0)
    return <Chip label="Sin stock" size="small" color="error" onClick={onClick} sx={sx} />;
  const label = formatStockCantidad(stock);
  if (stock < 10)
    return <Chip label={label} size="small" color="warning" onClick={onClick} sx={sx} />;
  return <Chip label={label} size="small" color="success" onClick={onClick} sx={sx} />;
};

// ── Helpers para resolución de precio/stock ──────────────────────────────────
async function resolverPrecioConLista(
  producto: any,
  nroLista?: string,
  opciones: {
    aplicarCotizacionMoneda?: boolean;
    fechaEmision?: Date | string;
    precioForzado?: number;
    asegurarCotizacionesDelDia?: (
      codigosMoneda: number[],
      fecha?: string | Date | null,
    ) => Promise<Record<number, number>>;
  } = {},
): Promise<{
  precio: number;
  conversion: Awaited<ReturnType<typeof resolverPrecioPedidoConMoneda>>['conversion'];
  codigoMoneda: number;
  umPrecioLista?: string;
}> {
  let precio = obtenerPrecioBaseProducto(producto);
  let codigoMonedaLista: number | null = null;
  let umPrecioLista: string | undefined;
  if (opciones.precioForzado != null && Number.isFinite(opciones.precioForzado)) {
    precio = opciones.precioForzado;
  }
  if (nroLista) {
    try {
      const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(
        nroLista,
        producto.codigoArticulo,
      );
      const res = resolverPrecioDesdeLista(producto, reg);
      if (res.tipo !== 'sinPrecio') {
        if (opciones.precioForzado == null) {
          precio = res.precioLista;
          // Solo aplica si el monto viene de la lista (no forzado ya convertido).
          umPrecioLista = (reg.um || '').trim() || undefined;
        }
        codigoMonedaLista = res.codigoMoneda;
      }
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (opciones.aplicarCotizacionMoneda && status !== 404) {
        throw new Error(`No se pudo obtener el precio de la lista para el artículo ${producto.codigoArticulo}.`);
      }
    }
  }
  if (!opciones.aplicarCotizacionMoneda) {
    return { precio, conversion: null, codigoMoneda: codigoMonedaLista ?? 0, umPrecioLista };
  }
  if (!opciones.fechaEmision) {
    throw new Error('Debe indicar la fecha de emisión para convertir el precio del artículo.');
  }

  const codigoMoneda = codigoMonedaLista ?? resolverCodigoMonedaProducto(producto);
  if (codigoMoneda > 0 && opciones.asegurarCotizacionesDelDia) {
    await opciones.asegurarCotizacionesDelDia([codigoMoneda], opciones.fechaEmision);
  }

  const precioConvertido = await resolverPrecioPedidoConMoneda(
    precio,
    producto,
    codigoMonedaLista,
    opciones.fechaEmision,
  );
  return {
    precio: precioConvertido.precio,
    conversion: precioConvertido.conversion,
    codigoMoneda: precioConvertido.codigoMoneda,
    umPrecioLista,
  };
}

async function obtenerStock(
  codigoArticulo: string,
  umLinea?: string,
): Promise<number | null> {
  return fetchStockDisponibleEnUm(codigoArticulo, umLinea);
}
// ─────────────────────────────────────────────────────────────────────────────

const FacturaPOSLineasGrid: React.FC<Props> = ({
  lineas,
  nroLista,
  contextoDescuento,
  contextoUm = 'factura',
  fechaEmision,
  aplicarCotizacionMoneda = false,
  tipoIvaEmision = '',
  condIvaEmisor,
  productosIvaCache = EMPTY_PRODUCTOS_CACHE,
  preciosConIva = false,
  readOnly = false,
  usarPreciosPersistidosEnLineas = false,
  onLineaChange,
  onLineaFilled,
  onProductoIvaLoaded,
  onEliminarLinea,
  onDuplicarLinea,
  onAgregarLineaVacia,
  emptyHint = 'Buscá un artículo arriba para agregarlo a la factura',
  tiposArticulo,
  enablePorcConversion = false,
  enableCantidadesPedido = false,
  loteAsignacion = null,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { asegurarCotizacionesDelDia, cotizacionDiaDialog } = useCotizacionDiaGuard();
  const tableRef = useRef<HTMLTableElement>(null);
  const lineasRef = useRef(lineas);
  lineasRef.current = lineas;
  const mostrarLeyendaImpuestos = preciosConIva && !esCondIvaMonotributo(condIvaEmisor);
  const navCols = enablePorcConversion
    ? [...NAV_COLS_BASE.slice(0, 5), 'porcConversion', ...NAV_COLS_BASE.slice(5)]
    : NAV_COLS_BASE;
  const mostrarColumnasCantidades =
    enableCantidadesPedido &&
    lineas.some((l) => (Number(l.cantidadesPES) || 0) > 0 || (Number(l.cantidadCanc) || 0) > 0);
  const umSnapshotKind: UmSnapshotKind =
    contextoUm === 'pedido' || contextoUm === 'remito' ? 'ped' : 'fac';
  const mostrarColumnasUmExtra = lineas.some((l) => showExtraPedFac(l, umSnapshotKind));
  const umExtraLabels = labelsExtra(umSnapshotKind);

  /** Cache de equivalencias-um por código (para chips de UM). */
  const [eqCache, setEqCache] = useState<Record<string, EquivalenciasUmDetalle>>({});
  const eqCacheRef = useRef(eqCache);
  eqCacheRef.current = eqCache;
  const eqFetchingRef = useRef<Set<string>>(new Set());
  /** Evita reintentos infinitos de stock al editar/ver documentos ya cargados. */
  const stockFetchRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const pendientes = lineas
      .filter((l) => lineaArticuloResuelto(l) && String(l.codigoArticulo ?? '').trim())
      .map((l) => String(l.codigoArticulo).trim())
      .filter((c, i, arr) => arr.indexOf(c) === i)
      .filter((c) => !eqCacheRef.current[c] && !eqFetchingRef.current.has(c));

    if (pendientes.length === 0) return;

    let cancelled = false;
    pendientes.forEach((codigo) => eqFetchingRef.current.add(codigo));

    void (async () => {
      const results = await Promise.all(
        pendientes.map(async (codigo) => {
          try {
            const detalle = await productoService.getEquivalenciasUmDetalle(codigo);
            return { codigo, detalle };
          } catch {
            return {
              codigo,
              detalle: { equivalencias: [] } as EquivalenciasUmDetalle,
            };
          }
        }),
      );
      if (cancelled) {
        pendientes.forEach((c) => eqFetchingRef.current.delete(c));
        return;
      }
      setEqCache((prev) => {
        const next = { ...prev };
        for (const { codigo, detalle } of results) {
          next[codigo] = detalle;
          eqFetchingRef.current.delete(codigo);
        }
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [lineas]);

  // Al editar/ver, las líneas vienen sin stockDisponible → badge "?". Completar en la UM de la línea.
  useEffect(() => {
    const pendientes = lineas.filter((l) => {
      const cod = String(l.codigoArticulo ?? '').trim();
      if (!cod || !lineaArticuloResuelto(l)) return false;
      if (l.stockDisponible != null && Number.isFinite(Number(l.stockDisponible))) return false;
      const key = `${l.id}:${cod}`;
      if (stockFetchRef.current.has(key)) return false;
      return true;
    });
    if (pendientes.length === 0) return;

    const keys = pendientes.map((l) => `${l.id}:${String(l.codigoArticulo).trim()}`);
    let cancelled = false;
    keys.forEach((key) => stockFetchRef.current.add(key));

    void (async () => {
      await Promise.all(
        pendientes.map(async (l) => {
          const cod = String(l.codigoArticulo).trim();
          const key = `${l.id}:${cod}`;
          try {
            let detalle = eqCacheRef.current[cod];
            if (!detalle) {
              detalle = await productoService.getEquivalenciasUmDetalle(cod);
              if (!cancelled) {
                setEqCache((prev) => (prev[cod] ? prev : { ...prev, [cod]: detalle! }));
              }
            }
            let um = String(l.um ?? '').trim();
            if (!um) {
              const r = resolverUmYPrecioDesdeDetalle(
                productosIvaCache[cod] ?? { codigoArticulo: cod },
                Number(l.precioUnitario) || 0,
                contextoUm,
                detalle,
              );
              um = r.um;
            }
            const stock = await fetchStockDisponibleEnUm(
              cod,
              um,
              detalle,
              productosIvaCache[cod],
            );
            if (cancelled) return;
            const patch: Partial<POSLinea> = { stockDisponible: stock };
            if (!String(l.um ?? '').trim() && um) {
              patch.um = um;
              Object.assign(patch, precioConAnclaUm(Number(l.precioUnitario) || 0, um));
            }
            onLineaFilled(l.id, patch);
          } catch {
            /* key queda en stockFetchRef: no reintentar en loop */
          }
        }),
      );
    })();

    return () => {
      cancelled = true;
      // Strict Mode / remount: liberar keys para que el siguiente efecto pueda reintentar.
      keys.forEach((key) => stockFetchRef.current.delete(key));
    };
  }, [lineas, contextoUm, onLineaFilled, productosIvaCache]);

  // Estado para resolución de código
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [searchState, setSearchState] = useState<{
    id: string;
    results: SuggestionItem[];
    anchorEl: HTMLElement | null;
  } | null>(null);
  const [modoBloqueoDis, setModoBloqueoDis] = useState<ModoBloqueoArtDiscontinuados>(1);

  // Estado para popover de stock por depósito
  const [stockPopover, setStockPopover] = useState<{
    codigoArticulo: string;
    descripcion: string;
    um?: string | null;
    anchorEl: HTMLElement | null;
  } | null>(null);
  const [codigoErrors, setCodigoErrors] = useState<Record<string, string>>({});
  const [info360Articulo, setInfo360Articulo] = useState<string | null>(null);
  const [semaforoByCodigo, setSemaforoByCodigo] = useState<Record<string, {
    coberturaDias: number;
    demandaMensual6m: number;
    leadTimeDias: number;
  }>>({});
  const [loteAutoOpenById, setLoteAutoOpenById] = useState<Record<string, number>>({});
  const [lineaMonedaInfo, setLineaMonedaInfo] = useState<POSLinea | null>(null);
  const [cantidadesDialog, setCantidadesDialog] = useState<{ lineaId: string; rowIdx: number } | null>(null);
  const [lineaCantidadFoco, setLineaCantidadFoco] = useState<{ id: string; rowIdx: number } | null>(null);
  const cantidadesDialogLineaIdRef = useRef<string | null>(null);
  const lineaCantidadSeleccionada = lineaCantidadFoco
    ? lineas.find((l) => l.id === lineaCantidadFoco.id)
    : undefined;
  const puedeAjustarCantidades = Boolean(
    lineaCantidadSeleccionada &&
    lineaArticuloResuelto(lineaCantidadSeleccionada) &&
    Number(lineaCantidadSeleccionada.cantidad) > 0,
  );
  const omitirProximoBlurCantidadRef = useRef(false);
  const loteControlPrevRef = useRef<Record<string, string>>({});
  const kitLoadInflightRef = useRef<Set<string>>(new Set());

  const promptLotesSiCorresponde = useCallback(
    (
      id: string,
      controlLoteSerie: unknown,
      cantidad: number,
      lotes?: LoteAsignacion[] | null,
      kitOpts?: { esKit?: boolean; lotesComponentes?: KitLoteComponenteLinea[] | null },
    ) => {
      if (!loteAsignacion || readOnly) return;
      const qty = Number(cantidad) || 0;
      if (qty <= 0) return;
      if (kitOpts?.esKit && Array.isArray(kitOpts.lotesComponentes)) {
        const incomplete = kitOpts.lotesComponentes.some((c) => {
          if (!productoControlaLote(c.controlLoteSerie)) return false;
          const qtyComp = (Number(c.cantidadPorKit) || 0) * qty;
          const status = getLoteChipStatus(true, qtyComp, c.lotes);
          return status === 'incomplete' || status === 'expired';
        });
        if (!incomplete) return;
        setLoteAutoOpenById((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
        return;
      }
      if (!productoControlaLote(controlLoteSerie)) return;
      const status = getLoteChipStatus(true, qty, lotes);
      if (status !== 'incomplete' && status !== 'expired') return;
      setLoteAutoOpenById((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
    },
    [loteAsignacion, readOnly],
  );

  useEffect(() => {
    if (!loteAsignacion || readOnly) return;
    for (const linea of lineas) {
      const codigo = String(linea.codigoArticulo ?? '').trim();
      if (!codigo || linea.lotesComponentes !== undefined) continue;
      const producto = productosIvaCache[codigo];
      const tipo = String(
        (producto as { tipoArticulo?: string } | undefined)?.tipoArticulo ?? '',
      )
        .trim()
        .toUpperCase();
      if (tipo && tipo !== 'KIT') {
        if (linea.esKit) onLineaFilled(linea.id, { esKit: false, lotesComponentes: undefined });
        continue;
      }
      if (tipo !== 'KIT' && !producto) continue;
      if (kitLoadInflightRef.current.has(linea.id)) continue;
      kitLoadInflightRef.current.add(linea.id);
      void buildKitLotesComponentes(codigo, producto)
        .then((kit) => {
          if (!kit.esKit) {
            onLineaFilled(linea.id, { esKit: false, lotesComponentes: undefined });
            return;
          }
          onLineaFilled(linea.id, {
            esKit: true,
            lotesComponentes: kit.lotesComponentes ?? [],
          });
          promptLotesSiCorresponde(linea.id, 'N', Number(linea.cantidad) || 0, undefined, {
            esKit: true,
            lotesComponentes: kit.lotesComponentes ?? [],
          });
        })
        .finally(() => {
          kitLoadInflightRef.current.delete(linea.id);
        });
    }
  }, [lineas, loteAsignacion, onLineaFilled, productosIvaCache, promptLotesSiCorresponde, readOnly]);

  useEffect(() => {
    if (!loteAsignacion || readOnly) return;
    const alive = new Set(lineas.map((l) => l.id));
    for (const key of Object.keys(loteControlPrevRef.current)) {
      if (!alive.has(key)) delete loteControlPrevRef.current[key];
    }
    for (const l of lineas) {
      const control = String(l.controlLoteSerie ?? 'N').trim().toUpperCase() || 'N';
      const prev = loteControlPrevRef.current[l.id];
      loteControlPrevRef.current[l.id] = control;
      if (!productoControlaLote(control)) continue;
      // Primera vez que vemos la línea con control L/S, o transición hacia L/S
      if (prev != null && prev === control) continue;
      promptLotesSiCorresponde(l.id, control, Number(l.cantidad) || 0, l.lotes);
    }
  }, [lineas, loteAsignacion, readOnly, promptLotesSiCorresponde]);

  const setCodigoError = useCallback((id: string, message: string) => {
    setCodigoErrors((prev) => ({ ...prev, [id]: message }));
  }, []);

  const clearCodigoError = useCallback((id: string) => {
    setCodigoErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const codigosResueltosKey = lineas
    .filter((l) => lineaArticuloResuelto(l) && l.codigoArticulo.trim())
    .map((l) => l.codigoArticulo.trim().toUpperCase())
    .sort()
    .filter((c, i, arr) => arr.indexOf(c) === i)
    .join('|');

  useEffect(() => {
    const codigosResueltos = codigosResueltosKey
      ? codigosResueltosKey.split('|')
      : [];

    if (codigosResueltos.length === 0) {
      setSemaforoByCodigo({});
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const semaforos = await productoService.getProductosInfo360Semaforo(codigosResueltos);
        if (cancelled) return;
        const next: Record<string, {
          coberturaDias: number;
          demandaMensual6m: number;
          leadTimeDias: number;
        }> = {};
        for (const s of semaforos) {
          next[s.codigoArticulo.trim().toUpperCase()] = {
            coberturaDias: s.coberturaDias,
            demandaMensual6m: s.demandaMensual6m,
            leadTimeDias: s.leadTimeDias,
          };
        }
        setSemaforoByCodigo(next);
      } catch {
        if (!cancelled) setSemaforoByCodigo({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [codigosResueltosKey]);

  const marcarErrorCodigo = useCallback(
    (id: string, message: string) => {
      onLineaFilled(id, datosArticuloVacios());
      setCodigoError(id, message);
    },
    [onLineaFilled, setCodigoError],
  );

  const marcarCodigoInexistente = useCallback(
    (id: string) => {
      marcarErrorCodigo(id, MSG_CODIGO_INEXISTENTE);
    },
    [marcarErrorCodigo],
  );

  const focusCell = useCallback((rowIdx: number, colName: string) => {
    const el = tableRef.current?.querySelector<HTMLInputElement>(
      `[data-row="${rowIdx}"][data-col="${colName}"]`,
    );
    el?.focus();
    el?.select();
  }, []);

  /** Última consulta de descuento por renglón: con ofertas por volumen cada tecla en cantidad dispara una. */
  const descuentoSeqRef = useRef(new Map<string, number>());

  const aplicarDescuentoBackend = useCallback(
    async (id: string, extras: Partial<POSLinea> = {}, producto?: unknown) => {
      if (readOnly) return;
      const linea = lineasRef.current.find((l) => l.id === id);
      if (!linea || linea.descuentoManual) return;
      const codigo = String(extras.codigoArticulo ?? linea.codigoArticulo ?? '').trim();
      if (!codigo) return;
      const seq = (descuentoSeqRef.current.get(id) ?? 0) + 1;
      descuentoSeqRef.current.set(id, seq);
      const prod = producto ?? productosIvaCache[codigo] ?? { codigoArticulo: codigo };
      const porc = await resolverPorcDescLinea(
        contextoDescuento,
        prod,
        Number(extras.cantidad ?? linea.cantidad) || 0,
        String(extras.um ?? linea.um ?? ''),
      );
      if (descuentoSeqRef.current.get(id) !== seq) return;
      if (lineasRef.current.find((l) => l.id === id)?.descuentoManual) return;
      onLineaFilled(id, { descuento: porc, descuentoManual: false });
    },
    [contextoDescuento, onLineaFilled, productosIvaCache, readOnly],
  );

  const handleCambiarUmLinea = useCallback(
    async (linea: POSLinea, umTo: string) => {
      if (readOnly) return;
      const umFrom = String(linea.um ?? '').trim();
      const umDest = String(umTo ?? '').trim();
      if (!umDest || mismaUm(umFrom, umDest)) return;

      const codigo = String(linea.codigoArticulo ?? '').trim();
      const detalle = eqCache[codigo];
      const eqs = detalle?.equivalencias ?? [];
      if (!detalle || eqs.length === 0) {
        enqueueSnackbar('El artículo no tiene equivalencias de UM cargadas.', { variant: 'warning' });
        return;
      }

      // Cantidad se mantiene. Precio desde ancla (lista / edición manual), no desde el redondeado visible.
      const umAncla = String(linea.umPrecioRef || umFrom || detalle.umBase || '').trim();
      const precioAncla = Number(
        linea.precioUmRef != null && Number.isFinite(linea.precioUmRef)
          ? linea.precioUmRef
          : linea.precioUnitario,
      );
      const umBase = String(detalle.umBase ?? '').trim();
      if (
        !umAncla ||
        factorCantidadEntreUm(umAncla, umDest, eqs, umBase || undefined) == null
      ) {
        enqueueSnackbar(`Sin equivalencia entre ${umFrom || umAncla || '—'} y ${umDest}.`, {
          variant: 'error',
        });
        return;
      }

      const precioUnitario = round2(
        precioEnUmDocumento(precioAncla, umDest, umBase || umAncla, eqs, umAncla),
      );
      let stockDisponible: number | null | undefined = linea.stockDisponible;
      try {
        stockDisponible = await fetchStockDisponibleEnUm(
          codigo,
          umDest,
          detalle,
          productosIvaCache[codigo],
        );
      } catch {
        /* conservar stock previo */
      }

      onLineaFilled(linea.id, {
        um: umDest,
        ...precioConAnclaUm(precioUnitario, umDest, precioAncla, umAncla),
        stockDisponible: stockDisponible ?? null,
        // Evita quedar con precio/subtotal final del documento origen en otra UM.
        precioUnitarioFinal: undefined,
        subtotalFinal: undefined,
      });

      if (!linea.descuentoManual && lineaArticuloResuelto(linea)) {
        void aplicarDescuentoBackend(linea.id, {
          codigoArticulo: codigo,
          um: umDest,
        });
      }
    },
    [readOnly, eqCache, enqueueSnackbar, onLineaFilled, productosIvaCache, aplicarDescuentoBackend],
  );

  const ctxDescuentoKey = JSON.stringify(contextoDescuento ?? {});
  const prevCtxDescuentoKey = useRef<string | null>(null);
  useEffect(() => {
    if (readOnly) return;
    if (prevCtxDescuentoKey.current === null) {
      prevCtxDescuentoKey.current = ctxDescuentoKey;
      return;
    }
    if (prevCtxDescuentoKey.current === ctxDescuentoKey) return;
    prevCtxDescuentoKey.current = ctxDescuentoKey;
    lineasRef.current.forEach((linea) => {
      if (linea.descuentoManual || !String(linea.codigoArticulo ?? '').trim()) return;
      void aplicarDescuentoBackend(linea.id);
    });
  }, [aplicarDescuentoBackend, ctxDescuentoKey, readOnly]);

  const obtenerMultiplicadorDisplay = useCallback(
    (codigoArticulo?: string, productoFallback?: unknown): number => {
      if (!preciosConIva) return 1;
      const codigo = String(codigoArticulo ?? '').trim();
      return multiplicadorIvaProducto(productosIvaCache[codigo] ?? productoFallback, tipoIvaEmision, condIvaEmisor);
    },
    [preciosConIva, productosIvaCache, tipoIvaEmision, condIvaEmisor],
  );

  // ── Resolución de código al presionar Enter ──────────────────────────────
  const handleResolveCodigo = useCallback(
    async (id: string, codigo: string, inputEl: HTMLElement | null) => {
      if (!codigo.trim()) return;
      setResolvingId(id);
      setSearchState(null);

      const rowIdx = lineas.findIndex((l) => l.id === id);

      try {
        const trimmed = codigo.trim();

        if (esEntradaCodigoBarra(trimmed)) {
          try {
            const apiOpts = await opcionesGetProductoDocumento('VENTAS');
            const resolved = await productoService.resolverCodigoBarra(trimmed, nroLista, apiOpts);
            const tipoResuelto = (resolved.producto as { tipoArticulo?: string | null } | undefined)?.tipoArticulo;
            if (!tipoArticuloPermitidoEnLinea(tipoResuelto, tiposArticulo)) {
              marcarErrorCodigo(id, mensajeTipoArticuloNoPermitido(tiposArticulo));
              return;
            }
            const decisionBarra = await evaluarUsoProductoDocumento(
              {
                codigoArticulo: resolved.codigoArticulo,
                tipoArticulo: tipoResuelto,
                descripcion: resolved.descripcion,
              },
              'VENTAS',
            );
            if (!decisionBarra.permitido) {
              marcarErrorCodigo(id, decisionBarra.mensaje);
              return;
            }
            const cod = String(resolved.codigoArticulo ?? '').trim();
            if (cod && resolved.producto) onProductoIvaLoaded?.(cod, resolved.producto);
            let productoUm: unknown = resolved.producto ?? { codigoArticulo: cod, um: resolved.um };
            if (aplicarCotizacionMoneda && !resolved.producto) {
              productoUm = await obtenerProductoParaDocumento(cod, 'VENTAS');
            }
            const precioResuelto = aplicarCotizacionMoneda
              ? await resolverPrecioConLista(productoUm, nroLista, {
                  aplicarCotizacionMoneda,
                  fechaEmision,
                  precioForzado: Number(resolved.precioUnitario) || 0,
                  asegurarCotizacionesDelDia,
                })
              : {
                  precio: Number(resolved.precioUnitario) || 0,
                  conversion: null,
                  codigoMoneda: 0,
                };
            const { um, precioUnitario, precioUmRef, umPrecioRef } = await resolverUmPrecioLinea(
              productoUm,
              precioResuelto.precio,
              contextoUm,
              precioResuelto.umPrecioLista,
            );
            const stock = await obtenerStock(cod, um);
            clearCodigoError(id);
            onLineaFilled(id, {
              codigoArticulo: resolved.codigoArticulo,
              descripcionArticulo: resolved.descripcion,
              cantidad: resolved.cantidad,
              ...precioConAnclaUm(precioUnitario, um, precioUmRef, umPrecioRef),
              ...(precioResuelto.conversion
                ? {
                    precioOriginalMoneda: precioResuelto.conversion.precioOriginal,
                    codigoMonedaPrecio: precioResuelto.codigoMoneda,
                    cotizacionMonedaPrecio: precioResuelto.conversion.cotizacion,
                    precioConvertidoBaseMoneda: precioResuelto.conversion.precioEnPesos,
                  }
                : {}),
              precioMinimo: precioMinimoDesdeProducto(resolved.producto ?? productoUm),
              um,
              stockDisponible: stock,
              ...loteFlagsParaLinea(resolved.producto ?? productoUm),
            });
            void aplicarDescuentoBackend(
              id,
              {
                codigoArticulo: resolved.codigoArticulo,
                cantidad: resolved.cantidad,
                um,
              },
              resolved.producto ?? productoUm,
            );
            const colFocus = resolved.origen.startsWith('plu_') ? 'precioUnitario' : 'cantidad';
            setTimeout(() => focusCell(rowIdx, colFocus), 60);
            return;
          } catch (err) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const apiMsg = getApiErrorMessage(err, MSG_CODIGO_INEXISTENTE);
            if (aplicarCotizacionMoneda && err instanceof Error) {
              marcarErrorCodigo(id, err.message);
              return;
            }
            if (status === 409 || status === 400) {
              marcarErrorCodigo(id, apiMsg);
            } else {
              marcarCodigoInexistente(id);
            }
            return;
          }
        }

        // Intento lookup exacto por código
        const producto = await obtenerProductoParaDocumento(trimmed, 'VENTAS');
        if (!tipoArticuloPermitidoEnLinea(producto.tipoArticulo, tiposArticulo)) {
          marcarErrorCodigo(id, mensajeTipoArticuloNoPermitido(tiposArticulo));
          return;
        }
        const cod = String(producto.codigoArticulo ?? codigo).trim();
        if (cod) onProductoIvaLoaded?.(cod, producto);
        const precioResuelto = await resolverPrecioConLista(producto, nroLista, {
          aplicarCotizacionMoneda,
          fechaEmision,
          asegurarCotizacionesDelDia,
        });
        const { um, precioUnitario, precioUmRef, umPrecioRef } = await resolverUmPrecioLinea(
          producto,
          precioResuelto.precio,
          contextoUm,
          precioResuelto.umPrecioLista,
        );
        const stock = await obtenerStock(producto.codigoArticulo, um);

        clearCodigoError(id);
        onLineaFilled(id, {
          codigoArticulo: producto.codigoArticulo,
          descripcionArticulo: producto.descripcion,
          ...precioConAnclaUm(precioUnitario, um, precioUmRef, umPrecioRef),
          ...(precioResuelto.conversion
            ? {
                precioOriginalMoneda: precioResuelto.conversion.precioOriginal,
                codigoMonedaPrecio: precioResuelto.codigoMoneda,
                cotizacionMonedaPrecio: precioResuelto.conversion.cotizacion,
                precioConvertidoBaseMoneda: precioResuelto.conversion.precioEnPesos,
              }
            : {}),
          precioMinimo: precioMinimoDesdeProducto(producto),
          um,
          stockDisponible: stock,
          ...loteFlagsParaLinea(producto),
        });
        void aplicarDescuentoBackend(id, { codigoArticulo: producto.codigoArticulo, um }, producto);

        // Posicionarse en cantidad
        setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const apiMsg = getApiErrorMessage(err, '');
        const localMsg = err instanceof Error ? err.message : '';
        if (
          localMsg.toLocaleLowerCase().includes('cotiz') ||
          localMsg.toLocaleLowerCase().includes('conversión') ||
          localMsg.toLocaleLowerCase().includes('precio de la lista') ||
          localMsg.includes('fecha de emisión')
        ) {
          marcarErrorCodigo(id, localMsg);
          return;
        }
        if (status === 409 && apiMsg) {
          marcarErrorCodigo(id, apiMsg);
          return;
        }
        if (
          localMsg.includes('discontinuado') ||
          localMsg.includes('inactivo') ||
          localMsg.includes('No se agregó')
        ) {
          marcarErrorCodigo(id, localMsg);
          return;
        }
        // No encontrado por código → buscar por descripción (solo para elegir artículo existente)
        try {
          const paramsBloqueo = await paramsBusquedaProductoDocumento('VENTAS');
          setModoBloqueoDis(paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1);
          const res = await productoService.getProductos({
            searchTerm: codigo.trim(),
            pageNumber: 1,
            pageSize: 8,
            ...(tiposArticulo ? { tiposArticulo } : {}),
            ...paramsBloqueo,
          });
          const items: any[] = filtrarProductosInactivos(res.items ?? []);
          const enriched: SuggestionItem[] = items.map((p) => ({
            codigoArticulo: p.codigoArticulo,
            descripcion: p.descripcion,
            precioUnitario: obtenerPrecioBaseProducto(p),
            um: p.um ?? p.unidadMedida ?? '',
            stockDisponible: null,
            productoIva: p,
            tipoArticulo: p.tipoArticulo ?? null,
          }));
          if (enriched.length === 1) {
            if (!tipoArticuloPermitidoEnLinea(enriched[0].tipoArticulo, tiposArticulo)) {
              marcarErrorCodigo(id, mensajeTipoArticuloNoPermitido(tiposArticulo));
              return;
            }
            if (esDiscontinuadoBloqueadoParaAgregar(enriched[0].tipoArticulo, paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1)) {
              marcarErrorCodigo(id, mensajeArticuloDiscontinuadoBloqueado(enriched[0].codigoArticulo));
              return;
            }
            // Un único resultado → seleccionar directo
            try {
              const productoUnico = await obtenerProductoParaDocumento(enriched[0].codigoArticulo, 'VENTAS');
              const codU = String(productoUnico.codigoArticulo ?? enriched[0].codigoArticulo).trim();
              if (codU) onProductoIvaLoaded?.(codU, productoUnico);
            } catch (selErr) {
              const selMsg = selErr instanceof Error ? selErr.message : '';
              if (selMsg) {
                marcarErrorCodigo(id, selMsg);
                return;
              }
            }
            let productoParaUm: unknown = enriched[0].productoIva ?? enriched[0];
            try {
              productoParaUm = await obtenerProductoParaDocumento(enriched[0].codigoArticulo, 'VENTAS');
            } catch (error) {
              if (aplicarCotizacionMoneda) {
                marcarErrorCodigo(
                  id,
                  error instanceof Error ? error.message : 'No se pudo cargar el artículo para convertir su precio.',
                );
                return;
              }
              /* usar datos de búsqueda */
            }
            const precioResuelto = await resolverPrecioConLista(productoParaUm, nroLista, {
              aplicarCotizacionMoneda,
              fechaEmision,
              asegurarCotizacionesDelDia,
            });
            const { um, precioUnitario, precioUmRef, umPrecioRef } = await resolverUmPrecioLinea(
              productoParaUm,
              precioResuelto.precio,
              contextoUm,
              precioResuelto.umPrecioLista,
            );
            const stock = await obtenerStock(enriched[0].codigoArticulo, um);
            clearCodigoError(id);
            onLineaFilled(id, {
              codigoArticulo: enriched[0].codigoArticulo,
              descripcionArticulo: enriched[0].descripcion,
              ...precioConAnclaUm(precioUnitario, um, precioUmRef, umPrecioRef),
              ...(precioResuelto.conversion
                ? {
                    precioOriginalMoneda: precioResuelto.conversion.precioOriginal,
                    codigoMonedaPrecio: precioResuelto.codigoMoneda,
                    cotizacionMonedaPrecio: precioResuelto.conversion.cotizacion,
                    precioConvertidoBaseMoneda: precioResuelto.conversion.precioEnPesos,
                  }
                : {}),
              precioMinimo: precioMinimoDesdeProducto(productoParaUm),
              um,
              stockDisponible: stock,
              ...loteFlagsParaLinea(productoParaUm),
            });
            void aplicarDescuentoBackend(
              id,
              { codigoArticulo: enriched[0].codigoArticulo, um },
              productoParaUm,
            );
            setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
          } else if (enriched.length > 1) {
            clearCodigoError(id);
            setSearchState({ id, results: enriched, anchorEl: inputEl });
          } else {
            marcarCodigoInexistente(id);
          }
        } catch (fallbackError) {
          const message = fallbackError instanceof Error ? fallbackError.message : '';
          if (
            message.toLocaleLowerCase().includes('cotiz') ||
            message.toLocaleLowerCase().includes('conversión') ||
            message.toLocaleLowerCase().includes('precio de la lista') ||
            message.includes('fecha de emisión')
          ) {
            marcarErrorCodigo(id, message);
            return;
          }
          marcarCodigoInexistente(id);
        }
      } finally {
        setResolvingId(null);
      }
    },
    [
      lineas,
      nroLista,
      contextoUm,
      fechaEmision,
      aplicarCotizacionMoneda,
      tiposArticulo,
      onLineaFilled,
      onProductoIvaLoaded,
      focusCell,
      clearCodigoError,
      marcarCodigoInexistente,
      marcarErrorCodigo,
      aplicarDescuentoBackend,
      asegurarCotizacionesDelDia,
    ],
  );

  const handleCodigoBlur = useCallback(
    async (id: string, codigo: string) => {
      const trimmed = codigo.trim();
      if (!trimmed || readOnly) return;
      const linea = lineas.find((l) => l.id === id);
      if (!linea || lineaArticuloResuelto(linea)) return;
      if (esEntradaCodigoBarra(trimmed)) {
        await handleResolveCodigo(id, trimmed, null);
        return;
      }
      try {
        await obtenerProductoParaDocumento(trimmed, 'VENTAS');
        await handleResolveCodigo(id, trimmed, null);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const apiMsg = getApiErrorMessage(err, '');
        const localMsg = err instanceof Error ? err.message : '';
        if (status === 409 && apiMsg) {
          marcarErrorCodigo(id, apiMsg);
        } else if (
          localMsg.includes('discontinuado') ||
          localMsg.includes('inactivo') ||
          localMsg.includes('No se agregó')
        ) {
          marcarErrorCodigo(id, localMsg);
        } else {
          marcarCodigoInexistente(id);
        }
      }
    },
    [lineas, readOnly, handleResolveCodigo, marcarCodigoInexistente, marcarErrorCodigo],
  );

  const handleSearchSelect = useCallback(
    async (item: SuggestionItem) => {
      if (!searchState) return;
      const { id } = searchState;
      if (!tipoArticuloPermitidoEnLinea(item.tipoArticulo, tiposArticulo)) {
        marcarErrorCodigo(id, mensajeTipoArticuloNoPermitido(tiposArticulo));
        setSearchState(null);
        return;
      }
      if (esDiscontinuadoBloqueadoParaAgregar(item.tipoArticulo, modoBloqueoDis)) {
        marcarErrorCodigo(id, mensajeArticuloDiscontinuadoBloqueado(item.codigoArticulo));
        setSearchState(null);
        return;
      }
      setSearchState(null);
      setResolvingId(id);
      const rowIdx = lineas.findIndex((l) => l.id === id);

      let producto: unknown = item.productoIva ?? item;
      try {
        producto = await obtenerProductoParaDocumento(item.codigoArticulo, 'VENTAS');
        const cod = String((producto as { codigoArticulo?: string }).codigoArticulo ?? item.codigoArticulo).trim();
        if (cod) onProductoIvaLoaded?.(cod, producto);
      } catch (selErr) {
        const selMsg = selErr instanceof Error ? selErr.message : '';
        if (selMsg) {
          marcarErrorCodigo(id, selMsg);
          setResolvingId(null);
          return;
        }
      }
      let precioResuelto: Awaited<ReturnType<typeof resolverPrecioConLista>>;
      try {
        precioResuelto = await resolverPrecioConLista(producto, nroLista, {
          aplicarCotizacionMoneda,
          fechaEmision,
          asegurarCotizacionesDelDia,
        });
      } catch (error) {
        marcarErrorCodigo(
          id,
          error instanceof Error ? error.message : 'No se pudo convertir el precio del artículo.',
        );
        setResolvingId(null);
        return;
      }
      const { um, precioUnitario, precioUmRef, umPrecioRef } = await resolverUmPrecioLinea(
        producto,
        precioResuelto.precio,
        contextoUm,
        precioResuelto.umPrecioLista,
      );
      const stock = await obtenerStock(item.codigoArticulo, um);
      clearCodigoError(id);
      onLineaFilled(id, {
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        ...precioConAnclaUm(precioUnitario, um, precioUmRef, umPrecioRef),
        ...(precioResuelto.conversion
          ? {
              precioOriginalMoneda: precioResuelto.conversion.precioOriginal,
              codigoMonedaPrecio: precioResuelto.codigoMoneda,
              cotizacionMonedaPrecio: precioResuelto.conversion.cotizacion,
              precioConvertidoBaseMoneda: precioResuelto.conversion.precioEnPesos,
            }
          : {}),
        precioMinimo: precioMinimoDesdeProducto(producto),
        um,
        stockDisponible: stock,
        ...loteFlagsParaLinea(producto),
      });
      void aplicarDescuentoBackend(id, { codigoArticulo: item.codigoArticulo, um }, producto);
      setResolvingId(null);
      setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
    },
    [
      searchState,
      modoBloqueoDis,
      lineas,
      nroLista,
      contextoUm,
      fechaEmision,
      aplicarCotizacionMoneda,
      tiposArticulo,
      onLineaFilled,
      onProductoIvaLoaded,
      focusCell,
      clearCodigoError,
      aplicarDescuentoBackend,
      marcarErrorCodigo,
      asegurarCotizacionesDelDia,
    ],
  );

  // ── Navegación por teclado ───────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      rowIdx: number,
      colName: string,
    ) => {
      if (e.key === 'Escape') {
        setSearchState(null);
        return;
      }
      if (e.key === 'F4' && enableCantidadesPedido && !readOnly && colName === 'cantidad') {
        e.preventDefault();
        const linea = lineas[rowIdx];
        if (linea && lineaArticuloResuelto(linea) && Number(linea.cantidad) > 0) {
          cantidadesDialogLineaIdRef.current = linea.id;
          setCantidadesDialog({ lineaId: linea.id, rowIdx });
        }
        return;
      }
      if (readOnly) return;
      const colIdx = navCols.indexOf(colName);

      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        setSearchState(null);
        const isLastCol = colIdx === navCols.length - 1;
        if (!isLastCol) {
          focusCell(rowIdx, navCols[colIdx + 1]);
        } else if (rowIdx < lineas.length - 1) {
          focusCell(rowIdx + 1, navCols[0]);
        } else {
          onAgregarLineaVacia();
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (colName === 'codigoArticulo') {
          const inputEl = (e.target as HTMLInputElement).closest(
            '.MuiFormControl-root',
          ) as HTMLElement | null;
          const id = lineas[rowIdx]?.id;
          if (id) handleResolveCodigo(id, (e.target as HTMLInputElement).value, inputEl);
        } else {
          if (rowIdx < lineas.length - 1) {
            focusCell(rowIdx + 1, navCols[0]);
          } else {
            onAgregarLineaVacia();
          }
        }
      }
    },
    [lineas, focusCell, onAgregarLineaVacia, handleResolveCodigo, readOnly, navCols, enableCantidadesPedido],
  );

  const handleChange = (id: string, field: keyof POSLinea, raw: string) => {
    if (field === 'codigoArticulo') {
      clearCodigoError(id);
      const linea = lineas.find((l) => l.id === id);
      onLineaChange(id, field, raw);
      if (linea && lineaArticuloResuelto(linea)) {
        onLineaFilled(id, datosArticuloVacios());
      }
      return;
    }
    if (NUMERIC_COLS.includes(field as string)) {
      const v = parseFloat(raw.replace(',', '.'));
      const numericValue = isNaN(v) ? 0 : v;
      onLineaChange(id, field, numericValue);
      if (field === 'cantidad') {
        const linea = lineas.find((l) => l.id === id);
        if (linea && !linea.descuentoManual && lineaArticuloResuelto(linea)) {
          void aplicarDescuentoBackend(id, { cantidad: numericValue });
        }
      }
    } else {
      onLineaChange(id, field, raw);
    }
  };

  const cellSx = {
    p: '3px 4px',
    border: 0,
    borderBottom: '1px solid',
    borderColor: 'divider',
    verticalAlign: 'middle',
  };
  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 1 } };
  const numInputProps = (rowIdx: number, col: string) => ({
    'data-row': rowIdx,
    'data-col': col,
    style: { textAlign: 'right' as const, padding: '4px 6px', fontSize: '0.82rem' },
    min: 0,
  });
  const textInputProps = (rowIdx: number, col: string) => ({
    'data-row': rowIdx,
    'data-col': col,
    style: { padding: '4px 6px', fontSize: '0.82rem' },
  });

  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.78rem',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  } as const;

  return (
    <Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer
          sx={{
            overflowX: 'auto',
            pb: 2,
            boxSizing: 'border-box',
          }}
        >
        <Table
          size="small"
          ref={tableRef}
          sx={{
            tableLayout: 'fixed',
            /* Evita que columnas colapsen y los títulos se superpongan en layouts angostos (p. ej. columna md ~65%). */
            minWidth:
              1040 +
              (enablePorcConversion ? 100 : 0) +
              (mostrarColumnasCantidades ? 180 : 0) +
              (mostrarColumnasUmExtra ? 140 : 0),
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ ...headerCellSx, width: 110 }}>Código</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 140, width: '22%' }}>Descripción</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 72, px: 0.5 }}>U/M</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 110, px: 0.5 }}>Stock</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 72 }}>Cant.</TableCell>
              {mostrarColumnasUmExtra && (
                <TableCell sx={{ ...headerCellSx, width: 72 }}>{umExtraLabels.cant}</TableCell>
              )}
              {mostrarColumnasUmExtra && (
                <TableCell sx={{ ...headerCellSx, width: 68, px: 0.5 }}>{umExtraLabels.um}</TableCell>
              )}
              {mostrarColumnasCantidades && (
                <TableCell sx={{ ...headerCellSx, width: 108, px: 0.5, textAlign: 'center' }}>En espera</TableCell>
              )}
              {mostrarColumnasCantidades && (
                <TableCell sx={{ ...headerCellSx, width: 108, px: 0.5, textAlign: 'center' }}>Cancelada</TableCell>
              )}
              <TableCell sx={{ ...headerCellSx, width: 140 }}>
                {preciosConIva ? 'Precio Final' : 'Precio'}
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 72 }}>Desc%</TableCell>
              {enablePorcConversion && (
                <TableCell sx={{ ...headerCellSx, width: 78 }}>
                  <Tooltip title="% Conv. del renglón (0–100). Es el mismo valor de la pantalla de conversión.">
                    <span>% Conv.</span>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell sx={{ ...headerCellSx, width: 100, textAlign: 'right' }}>
                {preciosConIva ? 'Subtotal Final' : 'Subtotal'}
              </TableCell>
              <TableCell sx={{ width: 56 }} />
              <TableCell sx={{ ...headerCellSx, width: 140 }}>Notas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={
                    10 +
                    (enablePorcConversion ? 1 : 0) +
                    (mostrarColumnasCantidades ? 2 : 0) +
                    (mostrarColumnasUmExtra ? 2 : 0)
                  }
                  align="center"
                  sx={{ py: 5, color: 'text.disabled', fontSize: '0.9rem' }}
                >
                  {emptyHint}
                </TableCell>
              </TableRow>
            )}
            {lineas.map((linea, rowIdx) => {
              const isResolving = resolvingId === linea.id;
              const multiplicadorDisplay = obtenerMultiplicadorDisplay(linea.codigoArticulo);
              const usarPreciosPersistidos =
                (readOnly || usarPreciosPersistidosEnLineas) &&
                preciosConIva &&
                linea.precioUnitarioFinal != null &&
                linea.precioUnitarioFinal > 0;
              const precioUnitarioDisplay = usarPreciosPersistidos
                ? linea.precioUnitarioFinal!
                : preciosConIva
                  ? aPrecioDisplay(linea.precioUnitario, multiplicadorDisplay)
                  : linea.precioUnitario;
              const subtotalDisplay =
                (linea.importeImportadoPdf ?? 0) > 0 && !(linea.descuento > 0)
                  ? round2(linea.importeImportadoPdf ?? 0)
                  : usarPreciosPersistidos
                    ? (linea.subtotalFinal ?? round2(linea.precioUnitarioFinal! * linea.cantidad))
                    : preciosConIva
                      ? subtotalLineaConIvaDisplay(
                          linea.precioUnitario,
                          linea.cantidad,
                          linea.descuento,
                          multiplicadorDisplay,
                        )
                      : linea.subtotal;
              const enEspera = Number(linea.cantidadesPES) || 0;
              const cancelada = Number(linea.cantidadCanc) || 0;
              const abrirCantidadesDeLinea = () => {
                if (readOnly || !lineaArticuloResuelto(linea) || !(Number(linea.cantidad) > 0)) return;
                cantidadesDialogLineaIdRef.current = linea.id;
                setCantidadesDialog({ lineaId: linea.id, rowIdx });
              };
              return (
                <TableRow key={linea.id} hover>
                  {/* ── Código (con resolución + spinner) ── */}
                  <TableCell sx={cellSx}>
                    <Box sx={{ position: 'relative' }}>
                      <CampoConTooltip title={linea.codigoArticulo}>
                        <TextField
                        size="small"
                        fullWidth
                        value={linea.codigoArticulo}
                        onChange={(e) => handleChange(linea.id, 'codigoArticulo', e.target.value)}
                        onBlur={(e) => void handleCodigoBlur(linea.id, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'codigoArticulo')}
                        inputProps={textInputProps(rowIdx, 'codigoArticulo')}
                        error={Boolean(codigoErrors[linea.id])}
                        InputProps={
                          isResolving
                            ? {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <CircularProgress size={12} />
                                  </InputAdornment>
                                ),
                                readOnly,
                              }
                            : { readOnly }
                        }
                        disabled={isResolving}
                        sx={{
                          ...inputSx,
                          m: 0,
                          '& input': { fontFamily: 'monospace' },
                        }}
                      />
                      </CampoConTooltip>
                      {codigoErrors[linea.id] ? (
                        <Typography
                          component="span"
                          variant="caption"
                          color="error"
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: '100%',
                            mt: 0.25,
                            fontSize: '0.65rem',
                            lineHeight: 1.1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {codigoErrors[linea.id]}
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>

                  {/* ── Descripción ── */}
                  <TableCell sx={{ ...cellSx, minWidth: 140 }}>
                    <CampoConTooltip title={linea.descripcionArticulo}>
                      <TextField
                        size="small"
                        fullWidth
                        value={linea.descripcionArticulo}
                        onChange={(e) =>
                          handleChange(linea.id, 'descripcionArticulo', e.target.value)
                        }
                        onKeyDown={(e) =>
                          handleKeyDown(e as any, rowIdx, 'descripcionArticulo')
                        }
                        inputProps={textInputProps(rowIdx, 'descripcionArticulo')}
                        InputProps={{ readOnly }}
                        sx={inputSx}
                      />
                    </CampoConTooltip>
                  </TableCell>

                  <TableCell sx={{ ...cellSx, verticalAlign: 'middle', px: 0.5, width: 72 }}>
                    <POSLineaUmChips
                      umActual={linea.um}
                      ums={listarUmsConEquivalencias(
                        eqCache[String(linea.codigoArticulo ?? '').trim()],
                        linea.um,
                      )}
                      disabled={readOnly || !lineaArticuloResuelto(linea)}
                      onSelect={(um) => {
                        void handleCambiarUmLinea(linea, um);
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, verticalAlign: 'middle', px: 0.5, width: 110 }}>
                    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
                      <StockBadge
                        stock={linea.stockDisponible}
                        onClick={
                          linea.codigoArticulo
                            ? (e) =>
                                setStockPopover({
                                  codigoArticulo: linea.codigoArticulo,
                                  descripcion: linea.descripcionArticulo,
                                  um: linea.um,
                                  anchorEl: e.currentTarget,
                                })
                            : undefined
                        }
                      />
                      {loteAsignacion && linea.esKit ? (
                        <KitLotesLineaButton
                          componentes={linea.lotesComponentes}
                          cantidadKits={Number(linea.cantidad) || 0}
                          nroUbicacion={loteAsignacion.nroUbicacion}
                          mode={loteAsignacion.mode}
                          disabled={readOnly}
                          iconOnly
                          autoOpenSignal={loteAutoOpenById[linea.id] || null}
                          onChange={(lotesComponentes) =>
                            onLineaFilled(linea.id, { esKit: true, lotesComponentes })
                          }
                        />
                      ) : null}
                      {loteAsignacion && !linea.esKit ? (
                        <LoteLineaButton
                          controlLoteSerie={linea.controlLoteSerie}
                          requiereVencimiento={linea.requiereVencimiento}
                          codigoArticulo={linea.codigoArticulo}
                          cantidad={Number(linea.cantidad) || 0}
                          nroUbicacion={loteAsignacion.nroUbicacion}
                          mode={loteAsignacion.mode}
                          lotes={linea.lotes}
                          lotesPermitidos={linea.lotesOrigenFactura}
                          disabled={readOnly}
                          iconOnly
                          autoOpenSignal={loteAutoOpenById[linea.id] || null}
                          onChange={(lotes: LoteAsignacion[]) => onLineaFilled(linea.id, { lotes })}
                        />
                      ) : null}
                      {lineaArticuloResuelto(linea) && linea.codigoArticulo.trim() ? (
                        <ProductoInfo360ListButton
                          producto={{
                            codigoArticulo: linea.codigoArticulo,
                            cantidad: linea.stockDisponible,
                            ...(semaforoByCodigo[linea.codigoArticulo.trim().toUpperCase()] ?? {}),
                          }}
                          onClick={() => setInfo360Articulo(linea.codigoArticulo.trim())}
                          dense
                        />
                      ) : null}
                    </Stack>
                  </TableCell>

                  {/* ── Cantidad ── */}
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={linea.cantidad || ''}
                      onChange={(e) => handleChange(linea.id, 'cantidad', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'cantidad')}
                      onFocus={() => {
                        if (!enableCantidadesPedido || readOnly || !lineaArticuloResuelto(linea)) return;
                        setLineaCantidadFoco({ id: linea.id, rowIdx });
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value.replace(',', '.');
                        const qty = parseFloat(raw);
                        const cantidad = Number.isFinite(qty) ? qty : Number(linea.cantidad) || 0;
                        const destino = e.relatedTarget as HTMLElement | null;
                        if (!destino?.closest('[data-ajustar-cantidades]')) {
                          setLineaCantidadFoco((actual) => (actual?.id === linea.id ? null : actual));
                        }
                        if (omitirProximoBlurCantidadRef.current) {
                          omitirProximoBlurCantidadRef.current = false;
                          return;
                        }
                        if (cantidadesDialogLineaIdRef.current === linea.id) return;
                        promptLotesSiCorresponde(
                          linea.id,
                          linea.controlLoteSerie,
                          cantidad,
                          linea.lotes,
                        );
                      }}
                      inputProps={numInputProps(rowIdx, 'cantidad')}
                      InputProps={{ readOnly }}
                      sx={inputSx}
                    />
                  </TableCell>

                  {mostrarColumnasUmExtra && (
                    <TableCell sx={{ ...cellSx, px: 0.5, textAlign: 'right' }}>
                      {showExtraPedFac(linea, umSnapshotKind)
                        ? (cantidadDoc(linea, umSnapshotKind) ?? 0).toLocaleString('es-AR', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 4,
                          })
                        : '—'}
                    </TableCell>
                  )}
                  {mostrarColumnasUmExtra && (
                    <TableCell sx={{ ...cellSx, px: 0.5 }}>
                      {showExtraPedFac(linea, umSnapshotKind)
                        ? umDoc(linea, umSnapshotKind) || '—'
                        : '—'}
                    </TableCell>
                  )}

                  {mostrarColumnasCantidades && (
                    <TableCell
                      onClick={abrirCantidadesDeLinea}
                      align="center"
                      sx={{
                        ...cellSx,
                        cursor: readOnly ? 'default' : 'pointer',
                      }}
                    >
                      <Tooltip title={readOnly ? '' : 'Modificar cantidades del pedido'}>
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <CantidadPedidoMarca valor={enEspera} tono="espera" />
                        </Box>
                      </Tooltip>
                    </TableCell>
                  )}
                  {mostrarColumnasCantidades && (
                    <TableCell
                      onClick={abrirCantidadesDeLinea}
                      align="center"
                      sx={{
                        ...cellSx,
                        cursor: readOnly ? 'default' : 'pointer',
                      }}
                    >
                      <Tooltip title={readOnly ? '' : 'Modificar cantidades del pedido'}>
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <CantidadPedidoMarca valor={cancelada} tono="cancelada" />
                        </Box>
                      </Tooltip>
                    </TableCell>
                  )}

                  {/* ── Precio ── */}
                  <TableCell sx={cellSx}>
                    <Stack direction="row" alignItems="center" spacing={0.25}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {usarPreciosPersistidos ? (
                          <TextField
                            size="small"
                            fullWidth
                            type="text"
                            value={precioUnitarioDisplay || ''}
                            onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'precioUnitario')}
                            inputProps={numInputProps(rowIdx, 'precioUnitario')}
                            InputProps={{ readOnly: true }}
                            sx={inputSx}
                          />
                        ) : (
                          <POSPrecioUnitarioField
                            precioNeto={linea.precioUnitario}
                            preciosConIva={preciosConIva}
                            multiplicador={multiplicadorDisplay}
                            onChange={(v) =>
                              onLineaFilled(linea.id, {
                                ...precioConAnclaUm(v, linea.um),
                              })
                            }
                            onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'precioUnitario')}
                            inputProps={numInputProps(rowIdx, 'precioUnitario')}
                            readOnly={readOnly}
                            sx={inputSx}
                          />
                        )}
                      </Box>
                      {linea.cotizacionMonedaPrecio != null &&
                        linea.precioOriginalMoneda != null &&
                        linea.precioConvertidoBaseMoneda != null &&
                        linea.codigoMonedaPrecio != null && (
                        <Tooltip title="Ver información de moneda">
                          <IconButton
                            size="small"
                            aria-label="Ver información de moneda"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLineaMonedaInfo(linea);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <CurrencyExchangeIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>

                  {/* ── Descuento ── */}
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={linea.descuento || ''}
                      onChange={(e) => handleChange(linea.id, 'descuento', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'descuento')}
                      inputProps={{ ...numInputProps(rowIdx, 'descuento'), max: 100 }}
                      InputProps={{ readOnly }}
                      sx={inputSx}
                    />
                  </TableCell>

                  {enablePorcConversion && (
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={linea.porcConversion || ''}
                        onChange={(e) => handleChange(linea.id, 'porcConversion', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'porcConversion')}
                        inputProps={{ ...numInputProps(rowIdx, 'porcConversion'), min: 0, max: 100, step: 0.0001 }}
                        InputProps={{ readOnly: readOnly || Number(linea.cantidadB ?? 0) > 0 }}
                        placeholder="%"
                        sx={inputSx}
                      />
                    </TableCell>
                  )}

                  <TableCell sx={{ ...cellSx, textAlign: 'right', verticalAlign: 'middle' }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={linea.subtotal > 0 ? 'text.primary' : 'text.disabled'}
                    >
                      {formatCurrency(subtotalDisplay)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ ...cellSx, verticalAlign: 'middle' }}>
                    <Stack direction="row" spacing={0} alignItems="center">
                      <Tooltip title="Duplicar línea">
                        <IconButton size="small" onClick={() => onDuplicarLinea(linea.id)} disabled={readOnly}>
                          <DuplicateIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onEliminarLinea(linea.id)}
                          disabled={readOnly}
                        >
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>

                  <TableCell sx={cellSx}>
                    <CampoConTooltip title={linea.notas}>
                      <TextField
                        size="small"
                        fullWidth
                        value={linea.notas ?? ''}
                        onChange={(e) => handleChange(linea.id, 'notas', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'notas')}
                        inputProps={textInputProps(rowIdx, 'notas')}
                        InputProps={{ readOnly }}
                        sx={inputSx}
                      />
                    </CampoConTooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      {/* ── Dropdown de búsqueda (Popper, fuera del TableContainer) ── */}
      {searchState && (
        <ClickAwayListener onClickAway={() => setSearchState(null)}>
          <Popper
            open
            anchorEl={searchState.anchorEl}
            placement="bottom-start"
            style={{ zIndex: 1400, minWidth: 380 }}
          >
            <Paper elevation={6} sx={{ borderRadius: 2, overflow: 'hidden', maxHeight: 300, overflowY: 'auto' }}>
              <List dense disablePadding>
                {searchState.results.map((s, idx) => {
                  const multiplicador = obtenerMultiplicadorDisplay(s.codigoArticulo, s.productoIva);
                  const precioDisplay = preciosConIva
                    ? aPrecioDisplay(s.precioUnitario, multiplicador)
                    : s.precioUnitario;
                  const disBloqueado = esDiscontinuadoBloqueadoParaAgregar(s.tipoArticulo, modoBloqueoDis);
                  const esDis = esTipoDiscontinuado(s.tipoArticulo);
                  return (
                    <ListItem
                      key={s.codigoArticulo}
                      disablePadding
                      divider={idx < searchState.results.length - 1}
                    >
                      <ListItemButton
                        disabled={disBloqueado}
                        onClick={() => handleSearchSelect(s)}
                        sx={{ py: 0.75, px: 1.5, opacity: disBloqueado ? 0.72 : 1 }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ minWidth: 80, fontFamily: 'monospace', flexShrink: 0 }}
                              >
                                {s.codigoArticulo}
                              </Typography>
                              <Typography variant="body2" flex={1} noWrap>
                                {s.descripcion}
                              </Typography>
                              {esDis ? (
                                <Chip
                                  label="Discontinuado"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              ) : null}
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="primary.main" fontWeight={600}>
                              {preciosConIva ? 'Precio Final: ' : ''}
                              {formatCurrency(precioDisplay)}
                              {mostrarLeyendaImpuestos ? ' c/imp.' : ''}
                              {s.um ? ` / ${s.um}` : ''}
                              {disBloqueado ? ' — no seleccionable' : ''}
                            </Typography>
                          }
                          disableTypography
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}

      <Box mt={1} display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Button size="small" startIcon={<AddIcon />} onClick={onAgregarLineaVacia} variant="text" disabled={readOnly}>
          Agregar línea vacía
        </Button>
        {enableCantidadesPedido && !readOnly && (
          <Tooltip
            title={
              puedeAjustarCantidades
                ? 'F4'
                : 'Seleccioná la cantidad de un artículo'
            }
          >
            <span>
              <Button
                data-ajustar-cantidades
                size="small"
                variant="outlined"
                disabled={!puedeAjustarCantidades}
                onMouseDown={(e) => e.preventDefault()}
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  if (!lineaCantidadFoco || !puedeAjustarCantidades) return;
                  cantidadesDialogLineaIdRef.current = lineaCantidadFoco.id;
                  setCantidadesDialog({
                    lineaId: lineaCantidadFoco.id,
                    rowIdx: lineaCantidadFoco.rowIdx,
                  });
                }}
              >
                Ajustar cantidades
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* ── Popover stock por depósito ── */}
      {stockPopover && (
        <FacturaPOSStockPopover
          codigoArticulo={stockPopover.codigoArticulo}
          descripcion={stockPopover.descripcion}
          umLinea={stockPopover.um}
          anchorEl={stockPopover.anchorEl}
          onClose={() => setStockPopover(null)}
        />
      )}

      <ProductoInfo360Dialog
        open={info360Articulo != null}
        codigoArticulo={info360Articulo}
        onClose={() => setInfo360Articulo(null)}
      />

      <Dialog
        open={lineaMonedaInfo != null}
        onClose={() => setLineaMonedaInfo(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
            Información de Moneda
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {lineaMonedaInfo && (() => {
            const moneda = lineaMonedaInfo.codigoMonedaPrecio === 1
              ? 'USD'
              : `Mon. ${lineaMonedaInfo.codigoMonedaPrecio}`;
            const precioOriginal = new Intl.NumberFormat('es-AR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(lineaMonedaInfo.precioOriginalMoneda ?? 0);
            const cotizacion = new Intl.NumberFormat('es-AR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            }).format(lineaMonedaInfo.cotizacionMonedaPrecio ?? 0);
            const precioArs = formatCurrency(lineaMonedaInfo.precioConvertidoBaseMoneda ?? 0);
            return (
              <>
                {(lineaMonedaInfo.codigoArticulo || lineaMonedaInfo.descripcionArticulo) && (
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    {[lineaMonedaInfo.codigoArticulo, lineaMonedaInfo.descripcionArticulo].filter(Boolean).join(' — ')}
                  </Typography>
                )}
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Precio Mon. Original:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{moneda} {precioOriginal}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Cotización Aplicada:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{cotizacion}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Precio en ARS:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{precioArs}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {moneda} {precioOriginal} x TC {cotizacion} = {precioArs} ARS
                </Typography>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setLineaMonedaInfo(null)}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ borderRadius: 999, px: 3, textTransform: 'uppercase', fontWeight: 700 }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {enableCantidadesPedido && (() => {
        const lineaDialog = cantidadesDialog
          ? lineas.find((l) => l.id === cantidadesDialog.lineaId)
          : undefined;
        const multDialog = lineaDialog ? obtenerMultiplicadorDisplay(lineaDialog.codigoArticulo) : 1;
        const cerrarCantidades = (lineaCerrar: POSLinea, cantidadParaLotes: number, rowIdx: number) => {
          omitirProximoBlurCantidadRef.current = true;
          cantidadesDialogLineaIdRef.current = null;
          setCantidadesDialog(null);
          promptLotesSiCorresponde(
            lineaCerrar.id,
            lineaCerrar.controlLoteSerie,
            cantidadParaLotes,
            lineaCerrar.lotes,
            { esKit: lineaCerrar.esKit, lotesComponentes: lineaCerrar.lotesComponentes },
          );
          if (!readOnly) setTimeout(() => focusCell(rowIdx, 'precioUnitario'), 60);
        };
        return (
          <CantidadesPedidoDialog
            open={Boolean(lineaDialog && cantidadesDialog)}
            descripcion={
              lineaDialog
                ? [lineaDialog.codigoArticulo, lineaDialog.descripcionArticulo].filter(Boolean).join(' — ')
                : ''
            }
            um={lineaDialog?.um}
            stockDisponible={lineaDialog?.stockDisponible}
            cantidad={Number(lineaDialog?.cantidad) || 0}
            cantidadesPES={Number(lineaDialog?.cantidadesPES) || 0}
            cantidadCanc={Number(lineaDialog?.cantidadCanc) || 0}
            importeDeCantidad={(cantidad) => {
              if (!lineaDialog) return 0;
              return preciosConIva
                ? subtotalLineaConIvaDisplay(
                    lineaDialog.precioUnitario,
                    cantidad,
                    lineaDialog.descuento,
                    multDialog,
                  )
                : aplicarPorcentajeDescuento(
                    round2((Number(cantidad) || 0) * (Number(lineaDialog.precioUnitario) || 0)),
                    lineaDialog.descuento,
                  );
            }}
            onCancel={() => {
              if (!lineaDialog || !cantidadesDialog) {
                cantidadesDialogLineaIdRef.current = null;
                setCantidadesDialog(null);
                return;
              }
              onLineaFilled(lineaDialog.id, { cantidadesPedidoRevisado: true });
              cerrarCantidades(lineaDialog, Number(lineaDialog.cantidad) || 0, cantidadesDialog.rowIdx);
            }}
            onAceptar={(valores) => {
              if (!lineaDialog || !cantidadesDialog) return;
              onLineaFilled(lineaDialog.id, {
                cantidad: valores.cantidad,
                cantidadesPES: valores.cantidadesPES,
                cantidadCanc: valores.cantidadCanc,
                cantidadesPedidoRevisado: true,
              });
              cerrarCantidades(lineaDialog, valores.cantidad, cantidadesDialog.rowIdx);
            }}
          />
        );
      })()}
      {cotizacionDiaDialog}
    </Box>
  );
};

export default FacturaPOSLineasGrid;
